import React, { useState, useMemo } from "react";
import { Card, DatePicker, Typography, Grid, Modal, Tag } from "antd";
import dayjs from "dayjs";
import styled from "styled-components";
import {
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import { getUserInfo, getUserList } from "redux/selectors";
import { connect, useSelector } from "react-redux";
import { bindActionCreators } from "redux";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const DateHeader = styled.div`
  display: grid;
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fafafa;
  border-bottom: 1px solid #e5e7eb;
`;

const DateCell = styled.div`
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  border-right: 1px solid #eee;
  background: ${(p) => (p.highlight ? "#CFF4E8" : "#fafafa")};
`;


const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const barColors = [
  "#22c55e",
  "#3b82f6",
  "#a855f7",
  "#f59e0b",
  "#ef4444",
  "#14b8a6",
];

const EngagementCalendar = ({ selectMonthYear, setSelectMonthYear, userList }) => {
  const userInfo = useSelector(getUserInfo);
  const selectedYear = selectMonthYear?.year || dayjs().year();
  const selectedMonth = selectMonthYear?.month || months[dayjs().month()];
  const monthIndex = months.indexOf(selectedMonth);

  const [selectedEngagement, setSelectedEngagement] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const startDate = dayjs(
    `${selectedYear}-${String(monthIndex + 1).padStart(2, "0")}-01`
  );

  const totalDays = startDate.daysInMonth();
  const startDatePayload = dayjs(
    `${selectedYear}-${String(monthIndex + 1).padStart(2, "0")}-01`
  ).format("DD-MM-YYYY");

  const endDatePayload = dayjs(
    `${selectedYear}-${String(monthIndex + 1).padStart(2, "0")}-01`
  )
    .endOf("month")
    .format("DD-MM-YYYY");

  const screens = useBreakpoint();
  const { key: engagementKey, url: engagementUrl } =
    queryKeys.getDashboardEngagement();

  const engagementPayload = {
    userid: userInfo?.userid,
    startdate: startDatePayload,
    enddate: endDatePayload,
    managingOffice: userInfo?.managingoffice?.[0] || "Zambia",
  };

  const { data: engagementDataRes } = useFetch(
    `${engagementKey}-${userInfo?.userid}-${selectedYear}-${selectedMonth}`,
    engagementUrl,
    {
      enabled: !!userInfo?.userid
    },
    engagementPayload
  );

  const engagementData = useMemo(() => {
    const engagements = engagementDataRes?.response?.data || [];

    return engagements.flatMap((engagement) => {
      const users = [
        ...(engagement.leads || []),
        ...(engagement.members || []),
      ];

      return users
        .filter(
          (user) =>
            user.userId === userInfo?.userid &&
            user.actualBookedStartDate &&
            user.actualBookedEndDate
        )
        .map((user) => ({
          ...engagement,
          ...user,
        }));
    });
  }, [engagementDataRes, userInfo]);


  const stackedRows = useMemo(() => {
    const rows = [];

    engagementData.forEach((item) => {
      const start = dayjs(item.actualBookedStartDate);
      const end = dayjs(item.actualBookedEndDate);

      let placed = false;

      for (let row of rows) {
        const overlap = row.some((r) => {
          const rStart = dayjs(r.actualBookedStartDate);
          const rEnd = dayjs(r.actualBookedEndDate);
          return !(end.isBefore(rStart) || start.isAfter(rEnd));
        });

        if (!overlap) {
          row.push(item);
          placed = true;
          break;
        }
      }

      if (!placed) {
        rows.push([item]);
      }
    });

    return rows;
  }, [engagementData]);

  const isMobile = !screens.md;
  const cellWidth = isMobile ? 60 : 80;
  const rowHeight = isMobile ? 42 : 50;

  const daysArray = useMemo(
    () =>
      Array.from({ length: totalDays }, (_, i) =>
        startDate.add(i, "day").format("DD")
      ),
    [totalDays, startDate]
  );

  const getBarStyle = (item, index) => {
    const color = barColors[index % barColors.length];

    if (!item.actualBookedStartDate || !item.actualBookedEndDate) return null;

    const start = dayjs(item.actualBookedStartDate);
    const end = dayjs(item.actualBookedEndDate);

    const monthStart = startDate.startOf("month");
    const monthEnd = startDate.endOf("month");

    if (end.isBefore(monthStart) || start.isAfter(monthEnd)) return null;

    const effectiveStart = start.isBefore(monthStart) ? monthStart : start;
    const effectiveEnd = end.isAfter(monthEnd) ? monthEnd : end;

    const leftOffset = effectiveStart.date() - 1;
    const duration = effectiveEnd.diff(effectiveStart, "day") + 1;

    return {
      position: "absolute",
      left: leftOffset * cellWidth,
      width: duration * cellWidth,
      height: rowHeight - 12,
      marginTop: 6,
      background: color,
      color: "white",
      borderRadius: 6,
      paddingLeft: 8,
      fontSize: isMobile ? 11 : 13,
      display: "flex",
      alignItems: "center",
      whiteSpace: "nowrap",
      cursor: "pointer",
    };
  };

  const handleBarClick = (item) => {
    setSelectedEngagement(item);
    setModalVisible(true);
  };

  const [currentMonthIndex, setCurrentMonthIndex] = useState(monthIndex);

  const goPrev = () => {
    setCurrentMonthIndex((prev) => {
      const newIndex = prev - 1;
      if (newIndex >= 0) {
        setSelectMonthYear((p) => ({ ...p, month: months[newIndex] }));
        return newIndex;
      }
      return prev;
    });
  };

  const goNext = () => {
    setCurrentMonthIndex((prev) => {
      const newIndex = prev + 1;
      if (newIndex <= 11) {
        setSelectMonthYear((p) => ({ ...p, month: months[newIndex] }));
        return newIndex;
      }
      return prev;
    });
  };

  const totalRows = Math.max(stackedRows.length, 5);

  const getUserFullName = (email) => {
    return (
      userList?.find((u) => String(u.userid) === String(email))?.fullname ||
      email ||
      "—"
    );
  };

  const partnerUser = selectedEngagement?.leads?.find(
    (lead) => lead.role?.toLowerCase().includes("partner")
  );

  const picUser = selectedEngagement?.leads?.find(
    (lead) => lead.role?.toLowerCase().includes("person")
  );

  const memberNames =
    selectedEngagement?.members?.map((m) =>
      getUserFullName(m.userId)
    ) || [];

  const generateSchedule = (start, end) => {
    if (!start || !end) return [];

    const startDate = dayjs(start);
    const endDate = dayjs(end);
    const days = [];

    let current = startDate;

    while (current.isBefore(endDate) || current.isSame(endDate)) {
      days.push(current);
      current = current.add(1, "day");
    }

    return days;
  };

  const schedule = generateSchedule(
    selectedEngagement?.actualBookedStartDate,
    selectedEngagement?.actualBookedEndDate
  );

  return (
    <Card bordered={false} style={{ borderRadius: 12 }}>
      {/* Header */}
      <Header>
        <Title level={5} style={{ margin: 0 }}>
          My Engagement ({engagementData.length})
        </Title>
        <Controls>
          <StyledYearPicker
            picker="year"
            value={dayjs(`${selectedYear}-01-01`)}
            onChange={(date) =>
              setSelectMonthYear((p) => ({
                ...p,
                year: date?.year(),
              }))
            }
          />

          <MonthNav>
            <NavArrow>
              <LeftOutlined onClick={goPrev} />
            </NavArrow>

            <MonthLabel>
              <CalendarOutlined />
              {months[currentMonthIndex]}
            </MonthLabel>

            <NavArrow>
              <RightOutlined onClick={goNext} />
            </NavArrow>
          </MonthNav>
        </Controls>
      </Header>

      {/* Calendar */}
      <ScrollWrap>
        <div style={{ minWidth: totalDays * cellWidth }}>

          {/* Sticky Date Header */}
          <DateHeader
            style={{
              gridTemplateColumns: `repeat(${totalDays}, ${cellWidth}px)`
            }}
          >
            {daysArray.map((d, i) => {
              const isToday =
                startDate.add(i, "day").format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD");

              return (
                <DateCell key={i} highlight={isToday}>
                  {d}
                </DateCell>
              );
            })}
          </DateHeader>

          {/* Calendar Grid */}
          <GridWrap
            style={{
              gridTemplateColumns: `repeat(${totalDays}, ${cellWidth}px)`,
              gridTemplateRows: `repeat(${totalRows}, ${rowHeight}px)`
            }}
          >
            {/* Empty grid cells */}
            {Array.from({ length: totalRows * totalDays }).map((_, i) => (
              <Cell key={i} />
            ))}

            {/* Engagement Bars */}
            {stackedRows.map((row, rowIndex) =>
              row.map((item, i) => {
                const style = getBarStyle(item, rowIndex + i);
                if (!style) return null;

                return (
                  <BarRow
                    key={`${item.clientId}-${rowIndex}-${i}`}
                    rowHeight={rowHeight}
                    style={{ top: rowIndex * rowHeight }}
                  >
                    <div
                      style={{ ...style, pointerEvents: "auto" }}
                      onClick={() => handleBarClick(item)}
                    >
                      {item.clientName}
                    </div>
                  </BarRow>
                );
              })
            )}
          </GridWrap>

        </div>
      </ScrollWrap>

      {/* ===== Engagement Details Modal ===== */}
      <Modal
        title={null}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={520}
        closable={false}
        styles={{ body: { padding: 0 } }}
        style={{ borderRadius: 12 }}
      >
        {selectedEngagement && (
          <ModalContainer>
            {/* Header */}
            <ModalHeader>
              <ModalTitle>Engagement Details</ModalTitle>
              <HeaderRight>
                {/* <ProgressPill>
                  Progress&nbsp;&nbsp;
                  <ProgressValue>
                    {selectedEngagement.personDays
                      ? `${Math.round((selectedEngagement.personDays / 42) * 100)}%`
                      : "75%"}
                  </ProgressValue>
                </ProgressPill> */}
                <CloseBtn onClick={() => setModalVisible(false)}>✕</CloseBtn>
              </HeaderRight>
            </ModalHeader>

            {/* Rows */}
            <ModalBody>
              <ModalRow>
                <ModalLabel>Client</ModalLabel>
                <ModalValue>{selectedEngagement.clientName || "N/A"}</ModalValue>
              </ModalRow>
              <ModalRow>
                <ModalLabel>Person In-Charge</ModalLabel>
                <ModalValue> {getUserFullName(picUser?.userId) || "N/A"}</ModalValue>

              </ModalRow>
              <ModalRow>
                <ModalLabel>Partner</ModalLabel>
                <ModalValue> {getUserFullName(partnerUser?.userId) || "N/A"}</ModalValue>
              </ModalRow>
              <ModalRow>
                <ModalLabel>Other Members</ModalLabel>
                <ModalValue> {memberNames.length ? memberNames.join(", ") : "N/A"}</ModalValue>
              </ModalRow>
              <ModalRow>
                <ModalLabel>Start Date</ModalLabel>
                <ModalValue>
                  {selectedEngagement.actualBookedStartDate
                    ? <>
                      {dayjs(selectedEngagement.actualBookedStartDate).format("DD MMM YYYY")}
                      {" "}&ndash;{" "}
                      <em>{dayjs(selectedEngagement.actualBookedStartDate).format("ddd")}</em>
                    </>
                    : "N/A"}
                </ModalValue>
              </ModalRow>
              <ModalRow>
                <ModalLabel>End Date</ModalLabel>
                <ModalValue>
                  {selectedEngagement.actualBookedEndDate
                    ? <>
                      {dayjs(selectedEngagement.actualBookedEndDate).format("DD MMM YYYY")}
                      {" "}&ndash;{" "}
                      <em>{dayjs(selectedEngagement.actualBookedEndDate).format("ddd")}</em>
                    </>
                    : "N/A"}
                </ModalValue>
              </ModalRow>
              <ModalRow>
                <ModalLabel>Total Booked Hours</ModalLabel>
                <ModalValue> {selectedEngagement?.budgetedHours || "0"}</ModalValue>
              </ModalRow>
              <ModalRow last>
                <ModalLabel>Hours Worked</ModalLabel>
                <ModalValue>{selectedEngagement?.totalWorkedHours || "0"}</ModalValue>
              </ModalRow>
            </ModalBody>
          </ModalContainer>
        )}
      </Modal>
    </Card>
  );
};
const mapStateToProps = (state) => ({
  userList: getUserList(state),

});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {

    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(EngagementCalendar);


/* ===================== STYLES ===================== */

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const Controls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const MonthNav = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;


const NavArrow = styled.div`
  cursor: pointer;
  color: #6b7280;
  font-size: 16px;   /* bigger */
  display: flex;
  align-items: center;

  &:hover {
    transform: scale(1.1);   /* nice UI effect */
  }
`;


const MonthLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #6b7280;   /* grey */
  font-weight: 500;
  font-size: 14px;

  svg {
    color: #6b7280;
  }
`;

const ScrollWrap = styled.div`
  overflow-x: auto;
  overflow-y: auto;
  height: 350px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  position: relative;
`;

const GridWrap = styled.div`
  display: grid;
  position: relative;
`;

const Cell = styled.div`
  border-right: 1px solid #eee;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;

  background: ${(p) =>
    p.highlight ? "#CFF4E8" : p.header ? "#fafafa" : "#fff"};

  font-weight: ${(p) => (p.header ? 600 : 400)};

  position: ${(p) => (p.header ? "sticky" : "relative")};
  top: ${(p) => (p.header ? "0" : "auto")};
  z-index: ${(p) => (p.header ? 10 : 1)};

  height: ${(p) => (p.header ? "50px" : "auto")}; /* important */
`;

const BarRow = styled.div`
  position: absolute;
  left: 0;
  height: ${props => props.rowHeight}px;
  display: flex;
  align-items: center;
  z-index: 3;
  pointer-events: none;
`;

const ModalContainer = styled.div`
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
`;

const ModalTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ProgressPill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #d1fae5;
  border-radius: 999px;
  padding: 3px 12px;
  font-size: 13px;
  color: #065f46;
  font-weight: 500;
`;

const ProgressValue = styled.span`
  font-weight: 700;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 15px;
  cursor: pointer;
  color: #9ca3af;
  padding: 0;
  line-height: 1;
  &:hover { color: #111; }
`;

const ModalBody = styled.div`
  padding: 0 20px 12px 20px;
`;

const ModalRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  align-items: center;
  padding: 10px 0;
  border-bottom: ${(p) => (p.last ? "none" : "1px solid #f3f4f6")};
`;

const ModalLabel = styled.span`
  color: #9ca3af;
  font-size: 13px;
`;

const ModalValue = styled.span`
  color: #111827;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
`;



const StyledYearPicker = styled(DatePicker)`
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  padding: 2px 12px;

  .ant-picker-input input {
    color: #374151;
    font-weight: 500;
  }

  &:hover {
    border-color: #d1d5db;
  }

  &.ant-picker-focused {
    border-color: #1677ff;
    box-shadow: none;
  }
`;

